'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import { cn } from '~/lib/utils';
import { BlockVideoFragment } from '~/server/generated/contentful-graphql';
import { numberOfSentences } from '~/utils/string';

import { BlockFooterCTA } from '../common/block-footer-cta';
import { SectionHeader } from '../common/section-header';

interface PlayerWrapperProps {
  children: React.ReactElement<any>;
}

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const PlayerWrapper = React.forwardRef((props: PlayerWrapperProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  return (
    <div className='relative pb-[56%]' ref={ref}>
      <div className='absolute inset-0'>{props.children}</div>
    </div>
  );
});

PlayerWrapper.displayName = 'PlayerWrapper';

export default function Video(props: Partial<BlockVideoFragment & { className?: string }>) {
  const {
    icon,
    headline,
    thumbnail,
    videoUrl,
    description,
    ctaHeadline,
    ctaTitle,
    ctaLink,
    ctaLinkContentCollection,
    ctaLinkAsset,
    autoplay,
    playerSize,
    className,
  } = props;

  const [videoIsPlaying, setVideoIsPlaying] = React.useState(false);
  const sentences = React.useMemo(() => (description ? numberOfSentences(description) : null), [description]);

  return (
    <div className={cn('container', playerSize === 'Large' ? 'lg:max-w-7xl' : 'md:max-w-5xl', className)}>
      <SectionHeader heading={headline} icon={icon} italicizeStarWars />
      {videoUrl && (
        <div className='overflow-hidden rounded-2xl'>
          <ReactPlayer
            config={{
              youtube: {
                color: 'white',
                origin: 'https://www.starlight.org',
              },
              vimeo: {
                controls: true,
              },
            }}
            controls
            playsInline
            playing={videoIsPlaying || !!autoplay}
            light={thumbnail && thumbnail.url ? thumbnail.url : false}
            playIcon={
              <svg
                className='sm:h-20 sm:w-20'
                fill='none'
                height='56'
                onClick={() => setVideoIsPlaying(true)}
                viewBox='0 0 72 72'
                width='56'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle cx='36' cy='36' fill='#0da5a5' r='36' />
                <path
                  d='M50.7227 33.435C51.3529 33.8104 51.7941 34.3734 52.0462 34.999C52.2983 35.6872 52.2983 36.3754 52.0462 37.001C51.7941 37.6891 51.3529 38.1896 50.7227 38.565L28.5378 51.5777C27.9076 51.9531 27.2143 52.0782 26.521 51.9531C25.7647 51.828 25.1975 51.5152 24.6933 50.9521C24.1891 50.4516 24 49.7634 24 49.0127V22.9873C24 22.174 24.2521 21.4858 24.7563 20.9853C25.2605 20.4848 25.8277 20.172 26.584 20.0469C27.2773 19.9218 27.9076 20.0469 28.5378 20.4223L50.7227 33.435Z'
                  fill='white'
                />
              </svg>
            }
            src={videoUrl}
            wrapper={PlayerWrapper as any}
          />
        </div>
      )}
      {sentences && (
        <p className={cn('body-base text-muted-foreground mx-auto mt-5 max-w-3xl', sentences === 1 && 'text-center')}>
          {description}
        </p>
      )}
      {ctaTitle && (
        <BlockFooterCTA
          ctaHeadline={ctaHeadline}
          ctaLink={ctaLink}
          ctaLinkAssetUrl={ctaLinkAsset?.url}
          ctaLinkContentSlug={ctaLinkContentCollection?.items[0]?.slug}
          ctaLinkContentType={ctaLinkContentCollection?.items[0]?.__typename}
          ctaTitle={ctaTitle}
        />
      )}
    </div>
  );
}
